/**
 * A source larger than one batch, applied as ordered parts.
 *
 * The server holds a batch to at most 100 rows, and a row's parents (a
 * course's term and faculty, an enrollment's student and course, an outcome's
 * enrollment) must be in the same payload, where an already recorded key
 * matches by its import identity. So each part here is closed over its
 * parents: a parent a later part needs is written into that part again, and
 * matches there the record an earlier part created. Nothing is matched by a
 * name, a title or an email; a parent is only the key a row writes.
 *
 * Rows sharing one key stay in one part, so the server still refuses them as
 * the duplicates they are rather than each part accepting one of them.
 *
 * Every part keeps the file's digest, because the digest names the file and
 * not the rows a part carries. The part's name is what tells its runs apart.
 *
 * The second half compares a part's fresh dry run, read just before it is
 * applied, with the projection the registrar reviewed. The only differences
 * that may pass are the ones earlier parts caused: a key going from would
 * create to matched because an earlier part created it, and a row whose
 * verdict stayed while its reason changed because a parent became real.
 */
import type { ImportKind, ImportReceipt, ImportRowInput, ReceiptAction } from "@/lib/api/imports";
import { composite, parentsOf } from "@/lib/refused-rows";

export const MAX_PART_ROWS = 100;

export interface ImportPart {
  /** 0-based position; parts apply in this order. */
  index: number;
  name: string;
  rows: ImportRowInput[];
  /** Keys this part carries again because a row in it needs them and an earlier part holds them. */
  repeated: string[];
}

/** A row whose own parent closure cannot fit one part. */
export interface PartProblem { key: string; rows: number }

export interface SourceParts { parts: ImportPart[]; problems: PartProblem[] }

export const compositeKey = (row: Pick<ImportRowInput, "entity_kind" | "import_key">) => composite(row.entity_kind, row.import_key);

export function partName(source: string, index: number, count: number): string {
  return `${source} · part ${index + 1} of ${count}`;
}

/** Each key's transitive parents present in the source. */
export function parentClosures(rows: ImportRowInput[], kind: ImportKind = "historical"): Map<string, Set<string>> {
  const direct = new Map<string, Set<string>>();
  for (const row of rows) {
    const key = compositeKey(row);
    const into = direct.get(key) ?? new Set<string>();
    for (const parent of parentsOf(kind, row.entity_kind, row.data ?? {})) if (parent !== key) into.add(parent);
    direct.set(key, into);
  }
  const closures = new Map<string, Set<string>>();
  const close = (key: string, trail: Set<string>): Set<string> => {
    const known = closures.get(key);
    if (known) return known;
    const all = new Set<string>();
    trail.add(key);
    for (const parent of direct.get(key) ?? []) {
      if (!direct.has(parent) || trail.has(parent)) continue;
      all.add(parent);
      for (const above of close(parent, trail)) all.add(above);
    }
    trail.delete(key);
    closures.set(key, all);
    return all;
  };
  for (const key of direct.keys()) close(key, new Set());
  return closures;
}

/**
 * Split rows into parts of at most `max` rows, each closed over its parents.
 *
 * Keys keep the file's order, except that a parent written after its child
 * moves before it. Parts fill with the rows no other row needs, in that order,
 * each with the parents the part still lacks; one starts a new part when they
 * no longer fit. A source of `max` rows or fewer is one part holding the rows
 * exactly as written.
 */
export function splitIntoParts(source: string, rows: ImportRowInput[], kind: ImportKind = "historical", max = MAX_PART_ROWS): SourceParts {
  if (rows.length <= max) return { parts: [{ index: 0, name: source, rows, repeated: [] }], problems: [] };
  const units = new Map<string, ImportRowInput[]>();
  for (const row of rows) {
    const key = compositeKey(row);
    const unit = units.get(key);
    if (unit) unit.push(row); else units.set(key, [row]);
  }
  const closures = parentClosures(rows, kind);
  const position = new Map([...units.keys()].map((key, i) => [key, i]));
  // Parents before children, otherwise the file's order.
  const order: string[] = [];
  const placed = new Set<string>();
  const place = (key: string) => {
    if (placed.has(key)) return;
    placed.add(key);
    [...(closures.get(key) ?? [])].sort((a, b) => position.get(a)! - position.get(b)!).forEach(place);
    order.push(key);
  };
  for (const key of units.keys()) place(key);
  const rank = new Map(order.map((key, i) => [key, i]));
  const size = (keys: Iterable<string>) => [...keys].reduce((n, key) => n + units.get(key)!.length, 0);

  // A part is filled by the rows nothing else needs, each brought with its parents, so a parent sits beside
  // its children and is carried again only when its children outgrow one part.
  const parents = new Set([...closures.values()].flatMap((c) => [...c]));
  const problems: PartProblem[] = [];
  const groups: Set<string>[] = [];
  const owner = new Map<string, number>();
  let current = new Set<string>();
  let count = 0;
  for (const key of order) {
    if (parents.has(key)) continue;
    const whole = [...(closures.get(key) ?? []), key];
    const full = size(whole);
    if (full > max) { problems.push({ key, rows: full }); continue; }
    let need = whole.filter((k) => !current.has(k));
    if (count + size(need) > max) {
      groups.push(current);
      current = new Set(); count = 0; need = whole;
    }
    for (const k of need) current.add(k);
    count += size(need);
  }
  if (current.size) groups.push(current);
  const parts = groups.map((group, index) => {
    const keys = [...group].sort((a, b) => rank.get(a)! - rank.get(b)!);
    const repeated = keys.filter((k) => owner.has(k));
    for (const k of keys) if (!owner.has(k)) owner.set(k, index);
    return { index, name: partName(source, index, groups.length), rows: keys.flatMap((k) => units.get(k)!), repeated };
  });
  return { parts, problems };
}

/** One row's verdict in a run, as the server binds an apply to it. */
export interface Verdict { action: ReceiptAction; resolved: string | null; reason: string | null; digest: string | null }

export function verdictsOf(receipts: ImportReceipt[]): Record<string, Verdict> {
  return Object.fromEntries(receipts.map((r) => {
    const digest = r.source_ref?._imam_row_sha256;
    return [compositeKey(r), { action: r.action, resolved: r.resolved_id ?? null, reason: r.reason ?? null, digest: typeof digest === "string" ? digest : null }];
  }));
}

export interface Drift { key: string; before: Verdict | null; after: Verdict | null }

/**
 * The differences between a part's reviewed projection and its fresh dry run
 * that earlier parts do not explain. Empty means the part may be applied.
 *
 * `created` holds the keys earlier parts wrote; `closures` each key's parents.
 */
export function unexplainedDrift(reviewed: Record<string, Verdict>, fresh: Record<string, Verdict>, created: ReadonlySet<string>,
  closures: ReadonlyMap<string, ReadonlySet<string>>): Drift[] {
  const keys = [...new Set([...Object.keys(reviewed), ...Object.keys(fresh)])].sort();
  const drift: Drift[] = [];
  for (const key of keys) {
    const before = reviewed[key] ?? null, after = fresh[key] ?? null;
    if (before && after && before.action === after.action && before.resolved === after.resolved && before.reason === after.reason && before.digest === after.digest) continue;
    if (before && after && before.digest === after.digest) {
      // An earlier part created this key, so it now matches that record.
      if (before.action === "would_create" && after.action === "matched" && before.resolved === null && created.has(key)) continue;
      // The verdict held; only its sentence changed, because a parent is now real.
      if (before.action === after.action && (before.action === "would_create" || before.action === "matched") && before.resolved === after.resolved
        && [...(closures.get(key) ?? [])].some((parent) => created.has(parent))) continue;
    }
    drift.push({ key, before, after });
  }
  return drift;
}

/** Each key once, by its verdict in the first part that carries it. */
export function combinedVerdicts(parts: Array<Record<string, Verdict>>): { verdicts: Map<string, Verdict>; repeated: number } {
  const verdicts = new Map<string, Verdict>();
  const repeated = new Set<string>();
  for (const part of parts) for (const [key, verdict] of Object.entries(part)) {
    if (verdicts.has(key)) repeated.add(key); else verdicts.set(key, verdict);
  }
  return { verdicts, repeated: repeated.size };
}
